import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useText } from '@src/components';
import {
  CustomForm,
  CustomFormItem,
  CustomNumberInput,
  CustomSpinSelect,
  OptionSpin,
  Text,
} from '@src/components';
import { Form, Row, Col, Button, Modal, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleLimitTypesDayRestrictionOperations,
  getVehicleLimitValueTypes,
  resetVehicleLimitWithValueTypes,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import {
  getVehicleList,
  getUpdateVehicleLimitsList,
  resetSelectedRowKeys,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementSlice';
import '@src/styles/addNewCar/addCarSetLimitType.scss';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { useLanguageChange } from '@src/hooks/useLanguageChange';

const AddVehicleLimitsListModal = ({
  isVisible,
  handleOk,
  handleCancel,
  vehicleDetailIds,
  plate,
  singleVehicle,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const {
    vehicleLimitTypesDayRestrictionOperations,
    vehicleLimitValueTypes,
    loading: loadingLimitType,
  } = useSelector((state) => state.addCarPlateDetail);
  const { vehicleDeviceTypeList } = useSelector((state) => state?.addCarType);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { loading: loadingVehicleLimitsList } = useSelector((state) => state.vehicleManagement);
  const isMobil = useMediaQuery({ query: '(max-width: 1199px)' });
  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const addLimitRestriction = useText('addLimitRestriction');
  const limitTypeText = useText('limitType');
  const limitValueTypeText = useText('limitValueType');
  const limitValueText = useText('limitValue');
  const uniqueProviderTypes = [4];

  const fetchVehicleLimitData = async () => {
    await Promise.all(
      uniqueProviderTypes?.map(async (providerType) => {
        await dispatch(getVehicleLimitTypesDayRestrictionOperations({ vouId: providerType }));
      }),
    );
    await dispatch(
      getVehicleLimitValueTypes({
        custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
      }),
    );
  };

  // The limit type / limit value type labels are backend text and are only fetched while the lists
  // are empty, so they have to be requested again when the language is switched.
  useLanguageChange(() => {
    fetchVehicleLimitData();
  });

  useEffect(() => {
    if (
      [vehicleLimitTypesDayRestrictionOperations, vehicleLimitValueTypes].every(
        (data) => data.length === 0,
      )
    ) {
      fetchVehicleLimitData();
    }
  }, [vehicleLimitTypesDayRestrictionOperations.length, vehicleLimitValueTypes.length, dispatch]);

  const validateLimitValue = (_, value) => {
    const limitTypeId = form.getFieldValue('limitTypeId');
    const limitValueTypeId = form.getFieldValue('limitValueTypeId');

    if (!limitTypeId || !limitValueTypeId) {
      return Promise.reject(new Error('Lütfen önce Limit Tipi ve Limit Değer Tipi seçin.'));
    }

    return Promise.resolve();
  };

  const onOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        const request = {
          vehicleDetailIds: singleVehicle?.vehicleDetailId
            ? [singleVehicle?.vehicleDetailId]
            : vehicleDetailIds,
          limitType: values.limitTypeId,
          limitValueType: values.limitValueTypeId,
          limitAmount: values.limitValue,
          plate: singleVehicle?.plate ?? plate,
        };
        const response = await dispatch(getUpdateVehicleLimitsList({ request })).unwrap();

        if (response.isSuccess) {
          api.success({
            message: addLimitRestriction,
            description: transactionSuccessfullyCompleted2,
            duration: 1,
          });
        }
        handleOk();
        form.resetFields();
        if (
          [vehicleLimitTypesDayRestrictionOperations, vehicleLimitValueTypes].some(
            (data) => data.length !== 0,
          )
        ) {
          dispatch(resetVehicleLimitWithValueTypes());
          dispatch(resetSelectedRowKeys());
        }
        setTimeout(async () => {
          if (isAdmin && selectedUserId) {
            await dispatch(getVehicleList({ customerId: selectedUserId }));
          } else {
            await dispatch(getVehicleList());
          }
        }, 1500);
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  const onCancel = () => {
    form.resetFields();
    handleCancel();
    if (
      [vehicleLimitTypesDayRestrictionOperations, vehicleLimitValueTypes].some(
        (data) => data.length !== 0,
      )
    ) {
      dispatch(resetVehicleLimitWithValueTypes());
    }
  };

  return (
    <div className="add-vehicle-modal" data-testid="add-vehicle-model-detail-id">
      {contextHolder}
      <Modal
        title={
          <span className="custom-modal-title">
            <Text t="addLimitRestriction" />
          </span>
        }
        visible={isVisible}
        onOk={onOk}
        onCancel={onCancel}
        width={1200}
        className="custom-modal"
        footer={[
          <div className="responsive-footer">
            <Button key="back" onClick={onCancel} className="responsive-footer-button">
              <Text t="giveUp" />
            </Button>

            <Button
              key="submit"
              type="primary"
              loading={loadingVehicleLimitsList.vehicleLimitsList}
              disabled={loadingVehicleLimitsList.vehicleLimitsList}
              onClick={onOk}
              data-testid="yes-button"
              className="responsive-footer-button"
            >
              <Text t="cardParameterSave" />
            </Button>
          </div>,
        ]}
      >
        <div style={{ padding: '30px 15px' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div className="add-car-set-limit-type">
                <CustomForm
                  name={`add-new-vehicle-details`}
                  className="filter-form"
                  autoComplete="off"
                  initialValues={{}}
                  layout={'horizontal'}
                  form={form}
                  // onValuesChange={handleValuesChange}
                >
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="form-item">
                    <Col span={isMobil ? 24 : 8}>
                      <CustomFormItem
                        label={<Text t="limitType" />}
                        name="limitTypeId"
                        className={'delivery-address-county-form-item'}
                      >
                        <CustomSpinSelect
                          id="add-car-set-limit-type-limitTypeId-select"
                          placeholder={limitTypeText}
                          disabled={vehicleLimitTypesDayRestrictionOperations.length === 0}
                          loading={loadingLimitType.vehicleLimitTypesDayRestrictionOperations}
                          height={36}
                        >
                          {vehicleLimitTypesDayRestrictionOperations?.map((vehicle) => (
                            <OptionSpin
                              id={`add-car-set-limit-type-limitTypeId-${vehicle?.limitTypeLabel}-option`}
                              key={vehicle?.limitTypeId}
                              value={vehicle?.limitTypeId}
                            >
                              {vehicle?.limitTypeLabel}
                            </OptionSpin>
                          ))}
                        </CustomSpinSelect>
                      </CustomFormItem>
                    </Col>

                    <Col span={isMobil ? 24 : 8}>
                      <CustomFormItem
                        label={<Text t="limitValueType" />}
                        name="limitValueTypeId"
                        className={'delivery-address-county-form-item'}
                      >
                        <CustomSpinSelect
                          id="add-car-set-limit-type-limitValueTypeId-select"
                          placeholder={limitValueTypeText}
                          disabled={vehicleLimitValueTypes.length === 0}
                          loading={loadingLimitType.vehicleLimitValueTypes}
                          height={36}
                        >
                          {vehicleLimitValueTypes?.map((vehicle) => (
                            <OptionSpin
                              id={`add-car-set-limit-type-limitValueTypeId-${vehicle?.limitValueTypeLabel}-option`}
                              key={vehicle?.id}
                              value={vehicle?.id}
                            >
                              {vehicle?.limitValueTypeLabel}
                            </OptionSpin>
                          ))}
                        </CustomSpinSelect>
                      </CustomFormItem>
                    </Col>

                    <Col span={isMobil ? 24 : 8}>
                      <CustomFormItem
                        label={<Text t="limitValue" />}
                        name="limitValue"
                        className={'delivery-address-county-form-item'}
                        rules={[
                          {
                            validator: validateLimitValue,
                          },
                        ]}
                      >
                        <CustomNumberInput
                          id="add-limit-value-numberInput"
                          autoComplete="off"
                          placeholder={limitValueText}
                          disabled={
                            vehicleDeviceTypeList.length === 0 &&
                            vehicleLimitTypesDayRestrictionOperations.length === 0
                          }
                          height={36}
                        />
                      </CustomFormItem>
                    </Col>
                  </Row>
                </CustomForm>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

AddVehicleLimitsListModal.propTypes = {
  isVisible: PropTypes.bool,
  handleOk: PropTypes.func,
  handleCancel: PropTypes.func,
  vehicleDetailIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  plate: PropTypes.string,
  singleVehicle: PropTypes.any,
};

export default AddVehicleLimitsListModal;
